import { ClientContext } from 'rc-app/components/Context';
import { IContext } from 'rc-app/types';
import React, { Component, createRef, RefObject, forwardRef, useState, useEffect } from 'react';
import { sdkKey } from '@mp/common';
import UserService from 'rc-app/services/UserService';
import { eventUpdateStream, getAgentByEmail, getProperties } from 'rc-app/Pages/Home';
import { useLocation, useNavigate } from "react-router-dom";
import Modal from '@mui/material/Modal';
import PropertyCard from './NetworkPropertySquare';
import { RCApp } from 'rc-app/RcMain';
import { World } from 'rc-app/Pages/World';

interface Props {
  sid: string;
  userCount: number;
  host: boolean;
  selectAnotherProperty?: boolean;
  setSelectAnotherProperty?: (value: boolean) => void;
  onStopped?: () => void;
  message?: string;
  propertyId?: string;
  onModelSet?: (sid: string, name: string) => void;
  open?: boolean;
  handleOpen?: () => void;
  handleClose?: () => void;
}

const Iframe = (props: any, ref: any) => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const channel = queryParams.get('channel');
  const [explore, setExplore] = useState(false);
  const [mpDigitalTwinList, setMpDigitalTwinList] = useState([]);

  const handleExploreOpen = () => setExplore(true);
  const handleExploreClose = () => setExplore(false);

  const handleExploreButton = (digital_twin_id: string, matterport_id: string) => {
    navigate(`/virtual-tour?propertyId=${matterport_id}&channel=${channel}`);
    props.setSelectAnotherProperty(false);
  };

  useEffect(() => {
    getProperties().then((res) => {
      setMpDigitalTwinList(res.mp_digital_twin_list);
    });
  }, []);

  return (
    <div className='w-full bg-[#F6F6F8]'>
      <div className='w-full flex gap-3 flex-col'>
        <div className='w-full flex bg-white p-3 h-full'>
          {!props.selectAnotherProperty && (
            <iframe
              id="sdk-iframe"
              className="flex w-full h-96"
              src={props.src}
              ref={ref}
              allow="xr-spatial-tracking"
              allowFullScreen={true}
            ></iframe>
          )}
          {props.selectAnotherProperty && (
            <div className='grid grid-cols-1 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-8'>
              {mpDigitalTwinList.map((mpDigitalTwin, index) => (
                <PropertyCard 
                  key={index}
                  className="w-full md:w-auto rounded-l"
                  imageUrl={mpDigitalTwin.property_thumbnail}
                  title={mpDigitalTwin.title}
                  bedrooms={mpDigitalTwin.characteristics.num_bedrooms}
                  area={mpDigitalTwin.characteristics.area_sqm}
                  city={mpDigitalTwin.address.city}
                  exploreButton={
                    <button 
                      type="button" 
                      className="bg-[#283BBE] w-full text-white font-semibold border-2 border-[#283BBE] rounded-md text-[10px] sm:text-[10px] md:text-[12px] lg:text-[15px] xl:text-[15px] px-4 h-[45px] sm:h-[40px]"
                      onClick={() => {
                        props.tourProperty.onStopped();
                        handleExploreButton(mpDigitalTwin._id, mpDigitalTwin.matterport_id);
                        handleExploreOpen();
                      }}
                    >
                      Explore
                    </button>
                  }
                />
              ))}
            </div>
          )}
        </div>
        <div className='flex w-1/5'>
          <button 
            type="button" 
            className="bg-[#283BBE] w-full text-white font-semibold border-2 border-[#283BBE] rounded-md text-[10px] sm:text-[10px] md:text-[12px] lg:text-[15px] xl:text-[15px] px-4 h-[45px] sm:h-[40px]"
            onClick={() => {
              props.tourProperty.onStopped();
              navigate('http://localhost:8000/');
            }}
          >
            Stop
          </button>
        </div>
      </div>

      {/* Choose Property Modal */}
      <Modal
        open={props.tourProperty.open}
        onClose={props.tourProperty.handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <div className="relative bg-white rounded-lg p-6 w-full max-w-md mx-auto border-2 border-black shadow-lg">
            <div className="flex flex-col gap-6">
              <div className='w-full flex flex-col gap-2'>
                <div className='flex items-center justify-center'>
                  <h1 className="font-bold text-black text-sm sm:text-base md:text-lg lg:text-xl xl:text-xl mr-5">
                    Choose another property
                  </h1>
                </div>
                <div className='flex items-center justify-center'>
                  <span className='items-center text-black text-xs sm:text-xs md:text-sm lg:text-sm xl:text-base'>
                    Do you really want to stop current property's virtual tour and choose a new property?
                  </span>
                </div>
              </div>
              <div className='w-full flex flex-col gap-3'>
                <div className='flex justify-center'>
                  <button
                    type="button"
                    className="bg-[#283BBE] w-full text-white font-semibold border-2 border-[#283BBE] rounded-md text-xs sm:text-xs md:text-sm lg:text-sm xl:text-base px-4 h-[45px] sm:h-[40px]"
                    onClick={() => {
                      props.setSelectAnotherProperty(true);
                      props.tourProperty.handleClose();
                    }}
                  >
                    Yes
                  </button>
                </div>
                <div className='flex justify-center'>
                  <button
                    type="button"
                    className="bg-[#FF307A] w-full text-white font-semibold border-2 border-[#EC0000] rounded-md text-xs sm:text-xs md:text-sm lg:text-sm xl:text-base px-4 h-[45px] sm:h-[40px]"
                    onClick={props.tourProperty.handleClose}
                  >
                    No
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Modal>

      {/* Explore Modal */}
      <Modal
        open={explore}
        onClose={handleExploreClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <div className="fixed inset-0 bg-white">
          <World Comp={<RCApp />} />
        </div>
      </Modal>
    </div>
  );
};

const IFrame2 = forwardRef(Iframe);

export class IFrameOverlay extends Component<Props, {}> {
  context: IContext;
  static contextType = ClientContext;
  private iframeRef: RefObject<HTMLIFrameElement>;
  private queryString: string = '';
  private apiHost: string = 'https://my.matterport.com';
  private email: string = UserService.getTokenParsed().email;

  constructor(props: Props) {
    super(props);
    this.state = {};
    this.onStopped = this.onStopped.bind(this);
    this.iframeRef = createRef();

    const urlParams = new URLSearchParams(window.location.search);
    urlParams.set('m', this.props.sid);
    this.apiHost = urlParams.get('apiHost') || 'https://my.matterport.com';
    urlParams.delete('apiHost');
    urlParams.delete('applicationKey');
    this.queryString = urlParams.toString();
  }

  componentDidMount() {
    if (this.props.host) {
      this.context.sdkHost.start(this.iframeRef.current);
    } else {
      this.context.sdkClient.start(this.iframeRef.current);
    }
  }

  componentWillUnmount() {
    if (this.props.host) {
      this.context.sdkHost.stop();
    } else {
      this.context.sdkClient.stop();
    }
  }

  onStopped(event: React.MouseEvent<HTMLButtonElement>) {
    if (this.props.onStopped) {
      this.props.onStopped();
      getAgentByEmail(this.email).then((data: any) => {
        const agent = data.agent;
        eventUpdateStream('PhotonSession', agent['_id'], this.email, 'leave_photon', 'true');
      });
    }
  }

  render(): JSX.Element {
    const url = `${this.apiHost}/show?${this.queryString}&play=1&qs=1&applicationKey=${sdkKey}`;
    return (
      <div className="flex flex-col bg-white shadow-md border-md">
        <div className='flex w-full'>
          <IFrame2 
            ref={this.iframeRef} 
            src={url} 
            tourProperty={this.props}
            selectAnotherProperty={this.props.selectAnotherProperty}
            setSelectAnotherProperty={this.props.setSelectAnotherProperty}
            message={this.props.message}
          />
        </div>
        {!this.props.selectAnotherProperty && (
          <div className='flex w-1/3 px-3 items-start justify-start'>
            <div className="flex w-full">
              <div className="font-bold text-black text-[14px] sm:text-[16px] md:text-[18px] lg:text-[20px] xl:text-[22px] mr-5">
                {this.props.host ? 'HOSTING' : 'OBSERVING'}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }
}